import { DependencyUsage } from '@modules/Dependencies/api/models/dependency-usage.model';
import React, { useEffect, useMemo } from 'react';
import { fadeIn } from 'react-animations';
import { useQuery } from '@tanstack/react-query';
import styled, { keyframes } from 'styled-components';

import Header from '../../../../components/dependencies/Header';
import SpinnerFullScreen from '../../../../components/dependencies/Spinner';
import { variables } from '../../../../components/variables';

import { useClientRect } from '../../../../hooks/useClientRect';
import Item, { ItemClickCallback } from './Item';
import { DataSource } from './useAppState';
import { getCurrentWorkflowFieldUsage } from './utils/workflow.utils';

const animation = {
    fadeInLeftBig: keyframes`${fadeIn}`,
};

const AnimatedFadeInDiv = styled.div`
    animation: 0.5s ${animation['fadeInLeftBig']};

    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    width: 100%;

    z-index: 20000;

    background-color: ${variables.colors.background};
    display: flex;
    flex-direction: column;
`;

const SectionStyles = styled.div`
    &&& {
        h5 {
            font-size: 13px;
            font-weight: bold;
            padding: 5px 0 10px 20px;
        }

        .children-container {
            font-size: 12px;
            padding: 0 0 10px 20px;
        }
    }
`;

const Section = React.forwardRef((props: any, ref: any) => {
    const { title, children, ...rest } = props;
    return (
        <SectionStyles ref={ref} {...rest}>
            <h5>{title}</h5>
            <div className="children-container">{children}</div>
        </SectionStyles>
    );
});

export type DepsInfoProps = {
    selectedDataSource: DataSource;
    onClose: (...args: any) => any;
    onItemClick: ItemClickCallback;
};

const DepsInfo = ({ selectedDataSource, onClose, onItemClick }: DepsInfoProps) => {
    const { isLoading, data, error } = useQuery({
        queryKey: ['dependencies-search'],
        queryFn: () => getCurrentWorkflowFieldUsage(selectedDataSource.Path),
        refetchOnWindowFocus: false,
    });

    useEffect(() => {
        if (error) {
            alert('Dependencies Navigation Error: ' + error.message);
        }
    }, [error]);

    const dependencies = useMemo(() => {
        return data?.map((dep) => ({
            ...dep,
            RuntimeName: selectedDataSource?.RuntimeName || '',
        })) as DependencyUsage[];
    }, [data, selectedDataSource?.RuntimeName]);

    const [correctDependenciesWrapperElRect, correctDependenciesWrapperElRef] = useClientRect(isLoading);

    return (
        <AnimatedFadeInDiv>
            <Header title={`"${selectedDataSource.Path}" Info:`} handleClose={onClose} />

            <div style={{ marginBottom: '10px' }} />

            <Section title="Type:">{selectedDataSource.Metadata.Type}</Section>

            {selectedDataSource.ArrayItemType && (
                <Section title="Type of array item:">{selectedDataSource.ArrayItemType}</Section>
            )}

            <Section title="Description:">{selectedDataSource.Metadata.Description}</Section>

            <Section title="Used in:" style={{ overflow: 'hidden', flexGrow: 1 }} ref={correctDependenciesWrapperElRef}>
                <div
                    style={{
                        overflow: 'auto',
                        height:
                            correctDependenciesWrapperElRect &&
                            Math.round(correctDependenciesWrapperElRect.height - 60),
                    }}
                    className="scrollbar"
                >
                    {isLoading && <SpinnerFullScreen />}

                    {!isLoading && dependencies && dependencies.length ? (
                        <>
                            {dependencies.map((dep, i) => (
                                <Item key={`${dep.Name}${dep.ItemType}${i}`} item={dep} onItemClick={onItemClick} />
                            ))}
                        </>
                    ) : null}
                </div>
            </Section>
        </AnimatedFadeInDiv>
    );
};

export default DepsInfo;
